import { useCallback, useEffect, useReducer, useRef } from 'react'
import { InteractionRequiredAuthError } from '@azure/msal-browser'
import { useAuthContext } from '@/auth/AuthProvider'
import { msalInstance, API_SCOPES } from '@/auth/msalConfig'
import { chatReducer, INITIAL_STATE, type ChatState, type StreamEvent } from './streamReducer'
import { dispatchToolCall } from './toolDispatcher'
import type { PendingFunctionCall } from './types'
import { foundryTransport } from './foundryTransport'
import type { ChatTransport } from './transport'

/**
 * Ceiling on tool-dispatch rounds per user turn. Eight is generous for the
 * current tool surface — intake reads a couple of lists then writes once —
 * while still terminating a model that has got stuck in a call/recall loop.
 */
const MAX_TOOL_ROUNDS = 8

/**
 * One line per model request so cache effectiveness is checkable in devtools.
 * `cacheRead > 0` from the second request of a turn onward is the signal that
 * the tools+instructions prefix is being served from cache rather than
 * re-billed as uncached input; a persistent `cacheRead=0` means the
 * breakpoints in `claudeTransport` aren't taking effect and the per-minute
 * quota is still exposed.
 */
function logUsage(usage: Extract<StreamEvent, { kind: 'stream.usage' }>): void {
  console.info(
    '[orion:chat] tokens in=%d out=%d cacheRead=%d cacheWrite=%d',
    usage.inputTokens,
    usage.outputTokens,
    usage.cacheReadInputTokens,
    usage.cacheCreationInputTokens,
  )
}

export interface UseChatResult extends ChatState {
  send: (message: string) => Promise<void>
  newThread: () => void
  cancel: () => void
  /**
   * Resolve the currently-pending tool approval. `approve: true` POSTs an
   * `mcp_approval_response` and streams the resumed run; `false` denies
   * the call locally and posts the denial so Foundry closes the run.
   * No-op if status is not `tool_approval_required`, or if the active
   * transport doesn't support approvals.
   */
  respondToApproval: (approve: boolean) => Promise<void>
  /**
   * Auto-approve future calls to `toolName` for the rest of this tab's
   * lifetime. Stored in memory only — never persisted to storage, so a
   * refresh resets the trust list. Idempotent.
   */
  trustToolForSession: (toolName: string) => void
  isToolTrusted: (toolName: string) => boolean
  /**
   * Open the OAuth consent flow in a popup, watch for the popup to close,
   * then re-send the user's last message chained on the consent-required
   * response id so Foundry retries `tools/list` with the now-cached token.
   * No-op unless status is `consent_required`.
   */
  authorizeAndRetry: () => void
  /**
   * Execute pending function-tool calls against the Orion API and POST
   * the results back. Normally invoked automatically when status flips to
   * `tool_outputs_required`; exposed here for tests and possible future
   * "user must confirm" flows.
   */
  submitToolOutputs: (pending: PendingFunctionCall[]) => Promise<void>
  /** Set when the transport's config (Foundry project/agent env vars) is missing. */
  configMissing: boolean
  /** Epoch ms of the current thread's first message; null before one is sent. */
  threadStartedAt: number | null
}

/**
 * Chat hook. Owns the reducer, the in-flight abort handle, the session
 * trust list, and the client-side tool dispatch — everything
 * model-agnostic. The *transport* owns talking to the model and turning its
 * wire protocol into `StreamEvent`s. Defaults to `foundryTransport`; pass a
 * different `ChatTransport` (e.g. a future `claudeTransport`) to swap the
 * backend with no change to the reducer or the render layer.
 */
export function useChat(transport: ChatTransport = foundryTransport): UseChatResult {
  const { authState } = useAuthContext()
  const [state, dispatch] = useReducer(chatReducer, INITIAL_STATE)
  const abortRef = useRef<AbortController | null>(null)
  // Session-scope auto-approve list. Lives in a ref so we don't rerender
  // when it changes; the reducer doesn't need to know about it.
  const trustedToolsRef = useRef<Set<string>>(new Set())
  // The most recent user input. Used to auto-resume after OAuth consent
  // — when the popup closes we re-send this same text chained on the
  // consent-required response id so Foundry retries `tools/list`.
  const lastUserMessageRef = useRef<string | null>(null)
  // The latest `state.status` for the auto-approve effect — using a ref
  // keeps the effect's deps stable while still letting it read fresh
  // status without firing on every state change.
  const statusRef = useRef(state.status)
  statusRef.current = state.status
  // When the current thread's first message was sent, for the thread label.
  // A ref (not reducer state) because it isn't part of the conversation the
  // model sees — and because this hook is mounted above the router, it
  // survives navigating away from /chat and back, which a ref inside
  // ChatView would not.
  const startedAtRef = useRef<number | null>(null)
  // Tool-dispatch rounds spent on the current user turn. The auto-dispatch
  // effect below is a loop — model asks for tools, we answer, model may ask
  // again — with nothing structural to stop it, and every round replays the
  // whole conversation to the model. Reset per user turn, not per thread.
  const toolRoundsRef = useRef(0)

  const configMissing = transport.configMissing

  // Conversation state is in-memory only. A page refresh starts a fresh
  // chain (no `previous_response_id`), which also forces Foundry to
  // refetch the MCP tool list — handy when we've just changed tool
  // names/descriptions on the server. Earlier we persisted `threadId`
  // to localStorage, which caused the messages/threadId asymmetry that
  // led to the hallucination bug (server-side context survived, UI
  // didn't). Removing the persistence is the cleaner fix.

  // Drive a transport's event stream into the reducer under a fresh
  // AbortController so `cancel()` / `newThread()` can interrupt it. The
  // transport yields `stream.error` for its own failures; the catch here
  // is a backstop for unexpected throws.
  const runStream = useCallback(
    async (makeEvents: (signal: AbortSignal) => AsyncIterable<StreamEvent>): Promise<void> => {
      const abort = new AbortController()
      abortRef.current = abort
      try {
        for await (const event of makeEvents(abort.signal)) {
          if (abort.signal.aborted) break
          if (event.kind === 'stream.usage') logUsage(event)
          dispatch(event)
        }
      } catch (err) {
        if (!(err instanceof Error && err.name === 'AbortError')) {
          dispatch({
            kind: 'stream.error',
            message: err instanceof Error ? err.message : 'Stream failed',
          })
        }
      } finally {
        abortRef.current = null
      }
    },
    [],
  )

  const send = useCallback(
    async (message: string): Promise<void> => {
      if (configMissing) return
      if (authState.status !== 'authenticated') return

      // Render the user message immediately so the UI doesn't wait on
      // the first SSE frame to show what the user just typed.
      const localId = `local-${Date.now()}`
      dispatch({ kind: 'user.message.appended', messageId: localId, text: message })
      lastUserMessageRef.current = message
      startedAtRef.current ??= Date.now()
      toolRoundsRef.current = 0

      const account = authState.account
      await runStream(signal =>
        transport.startTurn(message, {
          account,
          history: state.messages,
          threadId: state.threadId,
          signal,
        }),
      )
    },
    [authState, configMissing, transport, state.threadId, state.messages, runStream],
  )

  const respondToApproval = useCallback(
    async (approve: boolean): Promise<void> => {
      if (configMissing) return
      if (authState.status !== 'authenticated') return
      const status = state.status
      if (status.kind !== 'tool_approval_required') return
      const threadId = state.threadId
      if (!threadId) return
      const respond = transport.respondToApproval
      if (!respond) return

      // Optimistically transition: approve → sending (the new run will
      // promote the awaiting_approval row); deny → idle (the row turns
      // red).
      if (approve) {
        dispatch({ kind: 'tool_approval.submitted', approvalRequestId: status.approvalRequestId })
      } else {
        dispatch({ kind: 'tool_approval.denied', approvalRequestId: status.approvalRequestId })
      }

      const account = authState.account
      await runStream(signal =>
        respond(status.approvalRequestId, approve, {
          account,
          history: state.messages,
          threadId,
          signal,
        }),
      )
    },
    [authState, configMissing, transport, state.status, state.threadId, state.messages, runStream],
  )

  const resumeAfterConsent = useCallback(async (): Promise<void> => {
    // Re-fire the user's last input chained on the consent-required
    // response id. We do NOT dispatch `user.message.appended` — the
    // bubble already exists from the original send, and a duplicate
    // would look like the user re-typed the question.
    if (configMissing) return
    if (authState.status !== 'authenticated') return
    const threadId = state.threadId
    if (!threadId) return
    const lastMessage = lastUserMessageRef.current
    if (lastMessage === null) return
    const resume = transport.resumeAfterConsent
    if (!resume) return

    dispatch({ kind: 'thread.resume' })

    const account = authState.account
    await runStream(signal =>
      resume(lastMessage, { account, history: state.messages, threadId, signal }),
    )
  }, [authState, configMissing, transport, state.threadId, state.messages, runStream])

  /**
   * Execute every pending function-tool call against the Orion API, then
   * hand the results to the transport to resume the run.
   *
   * The dispatch tokens here are **API-scoped** (`API_SCOPES`) — the SPA
   * hits the Orion API directly rather than going through the MCP server.
   * That's the model-agnostic half and stays in the hook. The
   * model-scoped token for the resume POST is the transport's concern.
   * The MCP server stays in place for Claude.ai, which is the only
   * consumer that needs its OBO bridge.
   */
  const submitToolOutputs = useCallback(
    async (pending: PendingFunctionCall[]): Promise<void> => {
      if (configMissing) return
      if (authState.status !== 'authenticated') return
      if (pending.length === 0) return

      const account = authState.account
      const getApiToken = async (): Promise<string> => {
        try {
          const result = await msalInstance.acquireTokenSilent({
            scopes: API_SCOPES,
            account,
          })
          return result.accessToken
        } catch (err) {
          if (err instanceof InteractionRequiredAuthError) {
            const result = await msalInstance.acquireTokenPopup({
              scopes: API_SCOPES,
              account,
            })
            return result.accessToken
          }
          throw err
        }
      }

      // Dispatch all calls in parallel — most are reads, and even
      // writes are independent of each other within a single turn.
      const results = await Promise.all(
        pending.map(async call => {
          const result = await dispatchToolCall(call, getApiToken)
          dispatch({
            kind: 'tool_output.completed',
            callId: result.callId,
            ok: result.ok,
            resultJson: result.output,
          })
          return result
        }),
      )

      // Flip status to `sending` so the input bar reflects the resumed
      // run before the transport's first event arrives.
      dispatch({ kind: 'thread.resume' })
      await runStream(signal =>
        transport.submitToolOutputs(results, {
          account,
          history: state.messages,
          threadId: state.threadId,
          signal,
        }),
      )
    },
    [authState, configMissing, transport, state.threadId, state.messages, runStream],
  )

  const authorizeAndRetry = useCallback(() => {
    const status = state.status
    if (status.kind !== 'consent_required') return

    // Sized popup; positioned roughly centred on the current viewport.
    // Width/height match what Microsoft's consent host typically uses.
    const w = 520
    const h = 720
    const left = window.screenX + Math.max(0, (window.outerWidth - w) / 2)
    const top = window.screenY + Math.max(0, (window.outerHeight - h) / 2)
    const features = `width=${w},height=${h},left=${left},top=${top},scrollbars=yes,resizable=yes`
    const popup = window.open(status.consentLink, 'orion-consent', features)

    if (!popup) {
      // Popup blocked — fall back to a new tab. We can't auto-resume in
      // this path (no window handle to watch), so the user will need to
      // re-send manually after consenting. Surface a friendly error so
      // they know what happened.
      window.open(status.consentLink, '_blank', 'noopener,noreferrer')
      dispatch({
        kind: 'stream.error',
        message:
          'Popup was blocked — opened the consent flow in a new tab. ' +
          'After signing in, send your message again to continue.',
      })
      return
    }

    // Poll for popup close. We can't read inside (cross-origin) but
    // `.closed` is always accessible. 250 ms is responsive without
    // burning the event loop.
    const interval = window.setInterval(() => {
      if (popup.closed) {
        window.clearInterval(interval)
        void resumeAfterConsent()
      }
    }, 250)
  }, [state.status, resumeAfterConsent])

  const trustToolForSession = useCallback((toolName: string) => {
    trustedToolsRef.current.add(toolName)
  }, [])

  const isToolTrusted = useCallback((toolName: string) => {
    return trustedToolsRef.current.has(toolName)
  }, [])

  // Auto-approve effect: when status flips to tool_approval_required for
  // a tool the user has marked as trusted this session, click "approve"
  // ourselves so the user doesn't see the card flicker on every call.
  useEffect(() => {
    if (state.status.kind !== 'tool_approval_required') return
    if (!trustedToolsRef.current.has(state.status.toolName)) return
    void respondToApproval(true)
  }, [state.status, respondToApproval])

  // Auto-dispatch effect: when status flips to tool_outputs_required
  // (function-tool flow), execute the pending calls against the Orion
  // API and POST the results back. No user confirmation — the agent
  // already produced the call arguments, the user already signed in,
  // and the API enforces role/RLS at write time.
  // Capped: see MAX_TOOL_ROUNDS. Without the cap a model that keeps asking
  // for tools loops until something else breaks, and because every round
  // replays the full conversation, "something else" is usually the model
  // endpoint's per-minute token quota rather than anything we control.
  useEffect(() => {
    if (state.status.kind !== 'tool_outputs_required') return
    if (toolRoundsRef.current >= MAX_TOOL_ROUNDS) {
      dispatch({
        kind: 'stream.error',
        message: `Stopped after ${MAX_TOOL_ROUNDS} rounds of tool calls without a final answer. Try rephrasing, or send the message again.`,
      })
      return
    }
    toolRoundsRef.current += 1
    void submitToolOutputs(state.status.toolCalls)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run on status change only
  }, [state.status])

  const newThread = useCallback(() => {
    abortRef.current?.abort()
    lastUserMessageRef.current = null
    startedAtRef.current = null
    toolRoundsRef.current = 0
    dispatch({ kind: 'thread.reset' })
  }, [])

  const cancel = useCallback(() => {
    abortRef.current?.abort()
    abortRef.current = null
    // Surfacing the cancel as an error keeps UX honest — the assistant
    // bubble stops mid-stream and the run-status returns to idle on next send.
    dispatch({ kind: 'stream.error', message: 'Cancelled' })
  }, [])

  return {
    ...state,
    send,
    newThread,
    cancel,
    respondToApproval,
    trustToolForSession,
    isToolTrusted,
    authorizeAndRetry,
    submitToolOutputs,
    configMissing,
    threadStartedAt: startedAtRef.current,
  }
}
